import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Wrench, KeyRound, Sparkles, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatApiError } from "@/lib/utils2";

const BG_URL = "https://images.pexels.com/photos/4481945/pexels-photo-4481945.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200&h=1600";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Magic-link UI state
  const [showMagic, setShowMagic] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState("");

  const onLogin = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // True when the failed action was either:
  //  - login → wrong password (so they should try Forgot)
  //  - register → email already exists (likely they should sign in or reset)
  const showResetHint = !!error && (
    /invalid credentials/i.test(error) ||
    /already registered/i.test(error) ||
    /already exists/i.test(error)
  );

  const onRegister = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await register(regForm.email, regForm.password, regForm.name);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onMagicLink = async (e) => {
    e.preventDefault();
    setMagicError(""); setMagicBusy(true);
    try {
      await api.post("/auth/magic-link", { email: magicEmail });
      setMagicSent(true);
    } catch (err) {
      setMagicError(formatApiError(err));
    } finally {
      setMagicBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-5 bg-zinc-950 text-zinc-100">
      {/* Left artwork */}
      <div className="relative hidden lg:flex lg:col-span-3 overflow-hidden">
        <img
          src={BG_URL}
          alt="Mechanic garage"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 grid place-items-center bg-orange-600 rounded-sm">
              <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-2xl tracking-tighter">MAINTAIN<span className="text-orange-500">HQ</span></div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Equipment service log</div>
            </div>
          </div>
          <div className="max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-4">// SYSTEM 001 · OPERATOR LOGIN</div>
            <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
              Keep every <span className="text-orange-500">bolt</span>, belt & bearing on schedule.
            </h1>
            <p className="text-zinc-400 text-lg leading-relaxed max-w-md">
              From combines and tractors to dirt bikes and airplanes. One shop logbook. Every record. Every hour.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-0 border-t border-zinc-800">
            {[
              ["15+", "Equipment types"],
              ["PDF", "Export & reports"],
              ["0", "Missed oil changes"],
            ].map(([n, l]) => (
              <div key={l} className="py-4 px-1 border-r border-zinc-800 last:border-r-0">
                <div className="font-display font-black text-3xl text-orange-500">{n}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex flex-col justify-center p-6 sm:p-12 relative">
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <div className="h-11 w-11 grid place-items-center bg-orange-600 rounded-sm">
            <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter">MAINTAIN<span className="text-orange-500">HQ</span></div>
        </div>
        <div className="max-w-md w-full mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// ACCESS TERMINAL</div>
          <h2 className="font-display font-black text-3xl tracking-tight mb-8">
            {mode === "login" ? "Punch in." : "New operator."}
          </h2>

          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="w-full grid grid-cols-2 bg-zinc-900 rounded-sm p-1">
              <TabsTrigger value="login" data-testid="tab-login" className="rounded-sm data-[state=active]:bg-orange-600 data-[state=active]:text-white uppercase tracking-widest text-xs">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="rounded-sm data-[state=active]:bg-orange-600 data-[state=active]:text-white uppercase tracking-widest text-xs">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={onLogin} className="space-y-4">
                <div>
                  <Label htmlFor="li-email" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                  <Input
                    id="li-email"
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                    data-testid="login-email-input"
                    placeholder="operator@shop.com"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="li-password" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-orange-500"
                      data-testid="forgot-password-link"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <Input
                    id="li-password"
                    type="password"
                    required
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white uppercase tracking-widest font-bold rounded-sm py-6"
                  data-testid="login-submit-button"
                >
                  {busy ? "Checking..." : "Enter shop →"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={onRegister} className="space-y-4">
                <div>
                  <Label htmlFor="r-name" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</Label>
                  <Input
                    id="r-name"
                    required
                    value={regForm.name}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="r-email" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                  <Input
                    id="r-email"
                    type="email"
                    required
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="r-pwd" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password <span className="normal-case text-zinc-600 tracking-normal">(min 6)</span></Label>
                  <Input
                    id="r-pwd"
                    type="password"
                    required
                    minLength={6}
                    value={regForm.password}
                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                    data-testid="register-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white uppercase tracking-widest font-bold rounded-sm py-6"
                  data-testid="register-submit-button"
                >
                  {busy ? "Creating..." : "Clock in →"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {error ? (
            <div className="mt-4 border border-red-900/50 bg-red-950/40 text-red-300 px-3 py-3 text-sm rounded-sm space-y-2" data-testid="auth-error">
              <div>{error}</div>
              {showResetHint && (
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 font-bold uppercase tracking-widest text-xs"
                  data-testid="reset-from-error"
                >
                  <KeyRound className="w-3 h-3" /> Reset your password →
                </Link>
              )}
            </div>
          ) : null}

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <Button
            type="button"
            onClick={onGoogle}
            variant="secondary"
            className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-orange-600 text-white rounded-sm py-6 uppercase tracking-widest text-xs font-bold"
            data-testid="google-login-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.5L12 10.2z"/>
            </svg>
            Continue with Google
          </Button>

          {!showMagic ? (
            <button
              type="button"
              onClick={() => { setShowMagic(true); setMagicEmail(loginForm.email || regForm.email || ""); }}
              className="w-full mt-3 text-xs text-zinc-500 hover:text-orange-500 uppercase tracking-[0.3em] py-3 font-bold inline-flex items-center justify-center gap-2"
              data-testid="show-magic-link-button"
            >
              <Sparkles className="w-3 h-3" /> Or send me a magic link
            </button>
          ) : (
            <div className="mt-4 border border-zinc-800 bg-zinc-900/50 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-3 h-3 text-orange-500" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 font-bold">Magic sign-in</div>
              </div>
              {magicSent ? (
                <div className="flex items-start gap-3 text-sm text-zinc-300" data-testid="magic-sent-message">
                  <Mail className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    Link sent to <span className="text-white font-bold">{magicEmail}</span>. Click it on any device to sign in — expires in 30 min.
                  </div>
                </div>
              ) : (
                <form onSubmit={onMagicLink} className="space-y-3">
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Enter your email and we'll send a one-click sign-in link. No password needed.
                  </p>
                  <Input
                    type="email"
                    required
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    placeholder="operator@shop.com"
                    className="bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                    data-testid="magic-email-input"
                  />
                  {magicError ? (
                    <div className="border border-red-900/50 bg-red-950/40 text-red-300 px-3 py-2 text-xs rounded-sm" data-testid="magic-error">
                      {magicError}
                    </div>
                  ) : null}
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={magicBusy}
                      className="flex-1 bg-orange-600 hover:bg-orange-500 text-white uppercase tracking-widest text-xs font-bold rounded-sm"
                      data-testid="magic-send-button"
                    >
                      {magicBusy ? "Sending..." : "Send link →"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => { setShowMagic(false); setMagicError(""); setMagicSent(false); }}
                      className="text-xs text-zinc-500 hover:text-zinc-300 uppercase tracking-widest"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          <Link
            to="/forgot-password"
            className="block w-full mt-3 text-center bg-zinc-900/50 hover:bg-zinc-900 border border-dashed border-zinc-700 hover:border-orange-600 rounded-sm py-3 text-orange-500 hover:text-orange-400 font-bold text-xs uppercase tracking-[0.3em]"
            data-testid="forgot-password-bottom"
          >
            <KeyRound className="w-3 h-3 inline mr-1.5 -mt-0.5" /> Forgot your password?
          </Link>

          <div className="mt-8 text-center text-xs text-zinc-600">
            <Link to="/" className="hover:text-orange-500">← Back to landing</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
